import { useState, useEffect, useCallback } from "react";
import api from "../api";

// Fuente REAL: GET /noc/carriers. Arquitectura DIRECTA — los carriers pelan con el
// softswitch (Asterisk) directo; el SBC (Kamailio) NO interviene. El backend toma los
// carriers de netvoice.trunks y mide el estado con un SIP OPTIONS probe (responde = activo).
const REFRESH = 10;

// ── Iconos SVG ────────────────────────────────────────────────────────────────
const Ico = ({ d, size=14, color="currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d={d}/>
  </svg>
);
const ICON = {
  signal:  "M1.5 8.5s5-5 10.5-5 10.5 5 10.5 5M5 12s3.5-3.5 7-3.5S19 12 19 12M8.5 15.5s2-2 3.5-2 3.5 2 3.5 2M12 19h.01",
  refresh: "M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15",
  globe:   "M12 2a10 10 0 100 20A10 10 0 0012 2zM2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20",
  server:  "M2 2h20v8H2zM2 14h20v8H2zM6 6h.01M6 18h.01",
  warn:    "M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01",
};

// ── Status helpers ─────────────────────────────────────────────────────────────
function statusColor(st) {
  if (st === "active")   return "var(--success)";
  if (st === "inactive") return "var(--danger)";
  return "var(--warning)";
}
function statusBg(st) {
  if (st === "active")   return "var(--success-bg)";
  if (st === "inactive") return "var(--danger-bg)";
  return "var(--warning-bg)";
}
function statusLabel(st) {
  if (st === "active")   return "ACTIVO";
  if (st === "inactive") return "INACTIVO";
  return "PROBANDO";
}

function Badge({ status }) {
  return (
    <span style={{
      padding:"2px 10px", borderRadius:20, fontSize:11, fontWeight:700,
      letterSpacing:"0.07em", background: statusBg(status), color: statusColor(status),
      border:`1px solid ${statusColor(status)}`,
    }}>{statusLabel(status)}</span>
  );
}

function Tile({ label, value, color }) {
  return (
    <div style={{
      background:"var(--bg-void)", border:"1px solid var(--border)",
      borderRadius:"var(--r-sm)", padding:"10px 12px",
    }}>
      <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:"0.08em",
        color:"var(--text-muted)", marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:13, fontWeight:600, fontFamily:"var(--font-mono)",
        color: color||"var(--text-primary)" }}>{value}</div>
    </div>
  );
}

// ── Card de carrier (peering DIRECTO con el softswitch) ─────────────────────────
function CarrierCard({ c }) {
  const accent = statusColor(c.status);
  return (
    <div style={{
      background:"var(--bg-surface)", border:"1px solid var(--border)",
      borderRadius:"var(--r-lg)", padding:18, position:"relative", overflow:"hidden",
    }}>
      <div style={{ position:"absolute", top:0, left:0, right:0, height:3,
        background: accent, borderRadius:"var(--r-lg) var(--r-lg) 0 0" }}/>

      <div style={{ display:"flex", justifyContent:"space-between",
        alignItems:"flex-start", marginBottom:14 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
            <div style={{
              width:30, height:30, borderRadius:"var(--r-sm)",
              background:"var(--brand-subtle)",
              display:"flex", alignItems:"center", justifyContent:"center"
            }}>
              <Ico d={ICON.globe} size={14} color="var(--brand)"/>
            </div>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)" }}>{c.name}</div>
              <div style={{ fontSize:11, color:"var(--text-muted)", fontFamily:"var(--font-mono)" }}>
                {(c.transport||"udp").toUpperCase()} {c.ip}:{c.port}
              </div>
            </div>
          </div>
          <div style={{ fontSize:10, color:"var(--text-muted)", background:"var(--bg-raised)",
            display:"inline-block", padding:"1px 8px", borderRadius:"var(--r-sm)", marginLeft:38 }}>
            Carrier directo (LAN · IP-trust) · sin SBC
          </div>
        </div>
        <Badge status={c.status}/>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
        <Tile label="RTT (OPTIONS)"
          value={c.rtt != null ? `${c.rtt} ms` : "—"} color={statusColor(c.status)}/>
        <Tile label="Transporte" value={(c.transport||"udp").toUpperCase()}/>
        <Tile label="Prioridad"
          value={c.priority != null ? String(c.priority) : "—"}/>
      </div>

      {c.status === "active" && (c.ua || c.code) && (
        <div style={{ marginTop:10, fontSize:11, color:"var(--text-secondary)",
          fontFamily:"var(--font-mono)" }}>
          Respuesta SIP {c.code && c.code !== "?" ? c.code : "—"}{c.ua ? ` · ${c.ua}` : ""}
        </div>
      )}

      {c.status === "inactive" && (
        <div style={{
          marginTop:12, fontSize:11, color:"var(--warning)",
          background:"var(--warning-bg)", border:"1px solid rgba(245,166,35,0.3)",
          borderRadius:"var(--r-sm)", padding:"7px 10px",
          display:"flex", alignItems:"center", gap:6,
        }}>
          <Ico d={ICON.warn} size={12} color="var(--warning)"/>
          {c.error || "El carrier no responde OPTIONS"} — revisar peering/firewall del carrier (debe aceptar/responder desde 192.168.182.20).
        </div>
      )}
    </div>
  );
}

// ── Diagrama de RED: topología de BUS (todos en la misma LAN, directo por L2) ──────
// El Soft Switch (el servicio) + los carriers + el router cuelgan del backbone LAN. El
// link de bajada de cada nodo va VERDE si el servidor es alcanzable, ROJO si no
// (statusColor sobre su status). No es una cadena ni una estrella: es la LAN real.
function NetDiagram({ softswitch, carriers, router, swStatus, live }) {
  const sw = { key:"sw", icon:"\u26a1", name:"Soft Switch", service:true, status:swStatus,
    ip:`${softswitch ? softswitch.ip : "192.168.182.20"}:${softswitch ? softswitch.port : 5080}` };
  const devs = carriers.length
    ? carriers.map(c => ({ key:c.id, icon:"\ud83c\udf10", name:c.name, ip:`${c.ip}:${c.port}`, status:c.status }))
    : [{ key:"none", icon:"\ud83c\udf10", name:"Carrier", ip:"\u2014", status: live ? "probing" : "inactive" }];
  const nodes = [sw, ...devs];

  const boxW = 168, gap = 40, pad = 40, n = nodes.length;
  const W = Math.max(900, pad*2 + n*boxW + (n-1)*gap);
  const H = 292, busY = 116, busX0 = 34, busX1 = W - 34, routerX = W / 2;
  const rt = router || { name:"Router / Gateway", ip:"192.168.182.1", status: live ? "probing" : "inactive" };
  const step = boxW + gap, first = pad + boxW/2, cx = i => first + i*step;

  return (
    <div style={{ overflowX:"auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ height:"auto", display:"block" }}>
        {/* Router / Gateway */}
        <rect x={routerX-84} y={12} width={168} height={58} rx={10} fill="var(--bg-raised)" stroke="var(--border)"/>
        <text x={routerX} y={33} textAnchor="middle" fontSize={17}>{"\ud83d\udedc"}</text>
        <text x={routerX} y={49} textAnchor="middle" style={{ font:"600 12px sans-serif", fill:"var(--text-primary)" }}>{rt.name}</text>
        <text x={routerX} y={63} textAnchor="middle" style={{ font:"10px var(--font-mono)", fill:"var(--text-muted)" }}>{rt.ip}</text>
        <line x1={routerX} y1={70} x2={routerX} y2={busY-4} stroke={statusColor(rt.status)} strokeWidth={2.5}/>

        {/* Backbone LAN */}
        <rect x={busX0} y={busY-4} width={busX1-busX0} height={9} rx={4.5} fill="#3b82f6" opacity={0.85}/>
        <text x={busX0+6} y={busY-10} style={{ font:"700 11px var(--font-mono)", fill:"#3b82f6", letterSpacing:"0.06em" }}>LAN \u00b7 192.168.182.0/24</text>

        {/* Nodos: Soft Switch + carriers */}
        {nodes.map((d, i) => {
          const x = cx(i), bx = x - boxW/2, by = 150, pw = 88;
          const boxStroke = d.service ? "var(--brand)" : statusColor(d.status);
          const pillCol = d.service ? "var(--brand)" : statusColor(d.status);
          const pillBg  = d.service ? "var(--brand-subtle)" : statusBg(d.status);
          const pillTxt = d.service ? "SERVICIO" : statusLabel(d.status);
          return (
            <g key={d.key}>
              {/* link de bajada: verde=alcanzable, rojo=no alcanzable */}
              <line x1={x} y1={busY+5} x2={x} y2={by} stroke={statusColor(d.status)} strokeWidth={2.5}/>
              <rect x={bx} y={by} width={boxW} height={112} rx={10} fill="var(--bg-raised)" stroke={boxStroke} strokeWidth={d.service ? 1.6 : 1.2}/>
              <text x={x} y={by+30} textAnchor="middle" fontSize={21}>{d.icon}</text>
              <text x={x} y={by+54} textAnchor="middle" style={{ font:"600 13px sans-serif", fill:"var(--text-primary)" }}>{d.name}</text>
              <text x={x} y={by+71} textAnchor="middle" style={{ font:"10px var(--font-mono)", fill:"var(--text-muted)" }}>{d.ip}</text>
              <rect x={x-pw/2} y={by+84} width={pw} height={20} rx={10} fill={pillBg} stroke={pillCol}/>
              <text x={x} y={by+98} textAnchor="middle" style={{ font:"700 10px sans-serif", fill:pillCol }}>{pillTxt}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function CarrierMonitor() {
  const [softswitch, setSoftswitch] = useState(null);
  const [router,     setRouter]     = useState(null);
  const [carriers,   setCarriers]   = useState([]);
  const [live,       setLive]       = useState(false);
  const [secs,       setSecs]       = useState(REFRESH);
  const [loading,    setLoading]    = useState(true);
  const [err,        setErr]        = useState(null);
  const [ts,         setTs]         = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/noc/carriers");
      const d = r.data;
      if (d?.status !== "online") throw new Error("panel-api sin respuesta");
      setSoftswitch(d.softswitch || null);
      setRouter(d.router || null);
      setCarriers(d.carriers || []);
      setLive(true);
      setTs(new Date());
    } catch {
      setLive(false);
      setSoftswitch(null);
      setRouter(null);
      setCarriers([]);
      setErr("No se pudo leer el estado de carriers (panel-api) — estado no disponible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      setSecs(s => { if (s <= 1) { fetchData(); return REFRESH; } return s - 1; });
    }, 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const swStatus = softswitch?.status || (live ? "probing" : "inactive");

  return (
    <div style={{ maxWidth:1200 }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between",
        alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{
            width:38, height:38, borderRadius:"var(--r-md)",
            background:"linear-gradient(135deg,var(--brand),#0047b3)",
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>
            <Ico d={ICON.signal} size={18} color="#fff"/>
          </div>
          <div>
            <h1 style={{ fontSize:18, fontWeight:700, color:"var(--text-primary)", margin:0 }}>
              Monitor de Carriers SIP
            </h1>
            <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:2 }}>
              Softswitch eneural · interconexión <strong>directa</strong> con carriers (el SBC no interviene)
            </div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:8, height:8, borderRadius:"50%",
              background: live ? "var(--success)" : "var(--danger)" }}/>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.05em",
              color: live ? "var(--success)" : "var(--danger)" }}>
              {live ? "LIVE" : "SIN DATOS"}
            </span>
          </div>
          <span style={{ fontSize:11, color:"var(--text-muted)",
            background:"var(--bg-raised)", border:"1px solid var(--border)",
            borderRadius:20, padding:"3px 12px" }}>
            Actualiza en <strong style={{ color:"var(--brand)" }}>{secs}s</strong>
          </span>
          <button onClick={() => { fetchData(); setSecs(REFRESH); }}
            style={{
              background:"var(--brand-subtle)", color:"var(--brand)",
              border:"1px solid var(--border-active)", borderRadius:"var(--r-sm)",
              padding:"5px 14px", fontSize:12, cursor:"pointer",
              display:"flex", alignItems:"center", gap:5, fontWeight:500,
            }}>
            <Ico d={ICON.refresh} size={12}/> Actualizar
          </button>
        </div>
      </div>

      {/* Info chips */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 }}>
        {[
          ["Softswitch (Asterisk)", "192.168.182.20:5080"],
          ["Carriers (troncales)",  String(carriers.length)],
          ["Modo de conexión",      "directo · IP-trust"],
          ["Última verificación",   ts ? ts.toLocaleTimeString("es-EC",{hour12:false}) : "—"],
        ].map(([lbl,val]) => (
          <div key={lbl} style={{
            background:"var(--bg-surface)", border:"1px solid var(--border)",
            borderRadius:"var(--r-sm)", padding:"5px 12px",
            fontSize:11, color:"var(--text-secondary)",
          }}>
            {lbl}&nbsp;
            <span style={{
              fontFamily:"var(--font-mono)", fontSize:11, color:"var(--brand)",
              background:"var(--bg-void)", borderRadius:"var(--r-sm)", padding:"1px 6px",
            }}>{val}</span>
          </div>
        ))}
      </div>

      {/* Error banner */}
      {err && (
        <div style={{
          background:"var(--danger-bg)", border:"1px solid rgba(239,68,68,0.3)",
          borderRadius:"var(--r-md)", padding:"10px 14px", marginBottom:20,
          fontSize:12, color:"var(--danger)", display:"flex", alignItems:"center", gap:8,
        }}>
          <Ico d={ICON.warn} size={13} color="var(--danger)"/> {err}
        </div>
      )}

      {/* Diagrama de RED — misma LAN (directo, IP-trust) */}
      <div style={{ fontSize:11, fontWeight:600, textTransform:"uppercase",
        letterSpacing:"0.1em", color:"var(--text-muted)", marginBottom:12 }}>
        Red \u00b7 misma LAN (directo \u00b7 IP-trust)
      </div>
      <div style={{
        background:"var(--bg-surface)", border:"1px solid var(--border)",
        borderRadius:"var(--r-lg)", padding:"24px", marginBottom:20,
      }}>
        <NetDiagram softswitch={softswitch} carriers={carriers} router={router}
          swStatus={swStatus} live={live}/>
      </div>

      {/* Carrier cards */}
      <div style={{ fontSize:11, fontWeight:600, textTransform:"uppercase",
        letterSpacing:"0.1em", color:"var(--text-muted)", marginBottom:12 }}>
        Carriers interconectados (troncales en netvoice.trunks)
      </div>
      <div style={{
        display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(340px,1fr))",
        gap:16, marginBottom:20,
      }}>
        {loading && carriers.length === 0
          ? [1].map(i => (
              <div key={i} style={{ height:180, background:"var(--bg-surface)",
                border:"1px solid var(--border)", borderRadius:"var(--r-lg)", opacity:0.5 }}/>
            ))
          : carriers.length === 0
            ? <div style={{ color:"var(--text-muted)", fontSize:13, padding:"20px 0" }}>
                Sin troncales de carrier activas en <code>netvoice.trunks</code>.
              </div>
            : carriers.map(c => <CarrierCard key={c.id} c={c}/>)
        }
      </div>
    </div>
  );
}
